import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, hasStrokeDraw, repeatsAClass } from '../skillUsage';

export const chartStoryBuildSkill: CodeoSkill = {
    name: SKILL_NAMES.chartStoryBuild,
    kind: 'codeo',
    description:
        'Build a line or bar chart in the order that tells its story — axis, then the trend ' +
        'drawing, then the one point that matters called out — for a real series where the shape ' +
        'of the data is the argument.',
    checkLooksFor:
        'a series (<path>, <rect> or a repeated bar class) either dash-drawn, grown on scaleX/scaleY, or grown by attribute',
    // The axis is not checked for: it is as often a div or rect grown on scaleX as an SVG <line>,
    // and requiring the element blocked charts built in the order this skill asks for.
    documentUsesSkill: check(
        (script, markup) =>
            (/<(?:path|rect|polyline)\b/.test(markup) || repeatsAClass(markup)) &&
            (hasStrokeDraw(script) ||
                /scale[XY]/.test(script) ||
                /attr:\s*\{[^}]*\b(?:height|width|x2|y2|points|d)\s*:/.test(script)),
    ),
    instructions: `# Chart Story Build

A chart assembles in narrative order: the frame and axis establish the space, the series draws across it, and then one point is singled out. The order is the technique. A chart that appears all at once is a screenshot; a chart that builds tells the viewer where to look and when.

Use it when there is a real series and its *shape* is the point — growth, collapse, a crossover. For a single figure, use \`${SKILL_NAMES.counterTickUp}\`; for a proportion, \`${SKILL_NAMES.dataAsMetaphor}\` reads better than a chart.

## Markup

Author the series as one SVG path so it can be dash-drawn. Keep the axis minimal: a baseline and two labels — no grid, and no tick marks along it.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <div id="chart" data-l5-values="[12, 14, 13.6, 19, 23, 38]"
       style="position: relative; width: 1320px; height: 560px;">
    <svg class="overflow-visible" viewBox="0 0 1320 560" style="position: absolute; inset: 0; width: 1320px; height: 560px;">
      <line id="axis" x1="0" y1="520" x2="1320" y2="520" stroke="var(--l5-color-1, #c8d0dc)" stroke-width="3" />
      <!-- d and the peak dot are generated below from data-l5-values; never type them. The path
           carries data-l5-series to say it is the one drawn from the figures. -->
      <path id="series" data-l5-series fill="none" stroke="var(--l5-color-2, #38bdf8)" stroke-width="10" stroke-linecap="round" stroke-linejoin="round" />
      <circle id="series-peak" r="16" fill="var(--l5-color-2, #38bdf8)" />
      <text class="axis-end" x="20" y="556" font-family="var(--l5-font-0), sans-serif" font-size="34" fill="var(--l5-color-1, #c8d0dc)">Q1</text>
      <text class="axis-end" x="1160" y="556" text-anchor="end" font-family="var(--l5-font-0), sans-serif" font-size="34" fill="var(--l5-color-1, #c8d0dc)">Q6</text>
    </svg>
    <div id="peak-label" style="position: absolute; left: 1050px; top: -30px; font-family: var(--l5-font-0), sans-serif; font-size: 60px; font-weight: 800; color: var(--l5-color-0, #101820);">3.2×</div>
  </div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const chart = document.querySelector('#chart');
const seriesPath = document.querySelector('#series');

// The shape comes from the declared figures, so the line says what the data says.
const values = JSON.parse(chart.dataset.l5Values);
const PLOT_W = 1140, PLOT_H = 430, BASE_Y = 500, LEFT_X = 20;
const max = Math.max(...values);
const pointAt = (value, i) => [
  LEFT_X + (i / (values.length - 1)) * PLOT_W,
  BASE_Y - (value / max) * PLOT_H,
];
const points = values.map(pointAt);
seriesPath.setAttribute('d', points.map(([x, y], i) => (i ? 'L' : 'M') + x + ' ' + y).join(' '));
const [peakX, peakY] = points[values.indexOf(max)];
document.querySelector('#series-peak').setAttribute('cx', peakX);
document.querySelector('#series-peak').setAttribute('cy', peakY);

const seriesLength = Math.ceil(seriesPath.getTotalLength()) + 2;

gsap.set('#axis', { scaleX: 0, transformOrigin: 'left center' });
gsap.set('.axis-end', { opacity: 0 });
gsap.set(seriesPath, { strokeDasharray: seriesLength, strokeDashoffset: seriesLength + 1 });
gsap.set('#series-peak', { scale: 0, transformOrigin: 'center center' });
gsap.set('#peak-label', { y: 16, opacity: 0 });

tl.to('#axis', { scaleX: 1, duration: 0.5, ease: 'power3.out' }, 0.2);
tl.to('.axis-end', { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0.45);
tl.to(seriesPath, { strokeDashoffset: 0, duration: 1.5, ease: 'power2.inOut' }, 0.5);
tl.to('#series-peak', { scale: 1, duration: 0.34, ease: 'back.out(2)' }, 1.85);
tl.to('#peak-label', { y: 0, opacity: 1, duration: 0.42, ease: 'power3.out' }, 1.95);

window.__timelines = { main: tl };
\`\`\`

The series draws with \`power2.inOut\` — a constant-rate draw reads as a plotter, while easing in and out reads as a hand tracing the trend.

## Adapt

- **Bars instead of a line**: \`scaleY\` each bar from a \`bottom center\` origin with a 0.07s stagger, and put \`data-l5-bar\` on each bar so its height can be held to the figure it draws. Same three-beat order — see \`${SKILL_NAMES.statBarsAndFills}\` for the fuller treatment.
- **Decline**: the identical build with a downward series reads as a problem statement. Switch the accent to a warning colour and drop the peak dot to the *end* of the fall.
- **Two series**: draw the first fully, then the second, so the comparison is sequential. Simultaneous draws race and neither is legible. Declare each path's own \`data-l5-values\` on the path rather than on the container, so each line is held to its own figures.
- **Area fill**: a translucent polygon under the line, fading up as the stroke completes, gives the trend weight without adding a second read.
- **Crossover**: when two series cross, hold the draw at the crossing point for about 0.3s before continuing. That pause *is* the argument.
- **Axis labels**: exactly the two in the markup — a start and an end. Labelling every point turns the graphic into a dashboard; the shape of the line is what the viewer is reading, and six labels invite them to read values off it instead.

## Pitfalls

- \`getTotalLength()\` must be read from the live path after layout; dash values cannot be hardcoded. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- The axis and peak are \`scale\`-tweened, so set their \`transformOrigin\` through \`gsap.set\` and keep CSS \`transform\` off them. An SVG circle scaled without an explicit origin swings in from its box corner.
- Draw the series left to right, matching how the data is read. A path authored right-to-left dash-draws backwards.
- Two axis labels, not one per point. If the brief names six quarters, the first and last still carry the span — a label under every vertex is a table with a line drawn through it.
- Don't animate the axis, the series, the dot and the label at once. The order is the whole technique.
- Keep the series to one line and at most six vertices. A dense real-world series is unreadable at video pace.
- The label must not sit over the line. Place it clear of the path's bounding area, and give it \`data-l5-allow-offscreen\` if it reaches past the frame.
- Never fabricate a shape the data does not have. The build makes the trend persuasive, which is exactly why it has to be true. Declare the real figures on the chart container as \`data-l5-values\` — the same numbers you put in the plan's \`data\` — and generate every vertex from them. A typed \`d\` is a drawing of a trend, not a chart of one, and lint rejects it.`,
};
